import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Icon } from './Brand'

// Read-only view of the LIVE backend configuration (GET /api/config): which
// model provider is active, which correctness guardrails are on, and the real
// sub-agents + their granted capabilities from the registry.
export default function ControlsPage() {
  const [config, setConfig] = useState(null)
  const [error, setError] = useState('')
  const [scenarios, setScenarios] = useState([])

  useEffect(() => {
    let cancelled = false
    fetch('/api/config')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => { if (!cancelled) setConfig(data) })
      .catch(() => { if (!cancelled) setError('Could not load config — is the bridge running on :8080?') })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    fetch('/api/scenarios')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setScenarios(d.scenarios || []) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  return (
    <>
      <motion.div
        className="page-head"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      >
        <div>
          <div className="eyebrow">Configuration</div>
          <h1>Controls</h1>
          <p className="sub">The live backend configuration this assistant is running with.</p>
        </div>
      </motion.div>

      {error && <div className="chat-notice"><Icon name="info" />{error}</div>}

      {!config && !error && <div className="ph-sub">Loading configuration…</div>}

      {config && (
        <>
          <div className="section-label">Runtime</div>
          <div className="ctl-grid">
            <ControlTile icon="memory" label="Model provider" value={config.model_provider || '—'} tint="coral" />
            <ControlTile
              icon="verified_user"
              label="Fail-closed judge"
              value={config.judge_enabled ? 'On' : 'Off'}
              tint={config.judge_enabled ? 'teal' : 'lav'}
            />
            <ControlTile icon="security" label="Security sieve" value="Active" tint="teal" />
          </div>
          <div className="ph-sub" style={{ marginTop: 10 }}>
            <Icon name="shield" style={{ fontSize: 15, verticalAlign: '-3px', marginRight: 4 }} />
            Every request passes the security sieve before reaching an agent; blocked requests are routed to Approvals for a human decision.
          </div>

          <div className="section-label" style={{ marginTop: 22 }}>Eval scenarios ({scenarios.length})</div>
          <div className="panel">
            <div className="ph-sub">The golden questions the evaluator runs and grades — id, routed agent, and question.</div>
            <div className="feed">
              {scenarios.length === 0 && (
                <div className="ph-sub" style={{ marginBottom: 0 }}>No scenarios loaded.</div>
              )}
              {scenarios.map((s) => (
                <div className="feed-row" key={s.id}>
                  <div className="notif-body">
                    <div className="notif-title">{s.question}</div>
                    <div className="notif-text mono">{s.id}</div>
                  </div>
                  <div className="ctl-caps">
                    <span className={`ctl-cap ${s.agent ? '' : 'muted'}`}>{s.agent || 'out of scope'}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  )
}

function ControlTile({ icon, label, value, tint }) {
  return (
    <div className="kpi ctl-tile">
      <div className="ic" style={{ background: `var(--${tint}-50)` }}>
        {/* lav scale stops at -400; fall back so the icon color is always defined */}
        <Icon name={icon} style={{ color: `var(--${tint}-500, var(--${tint}-400))` }} />
      </div>
      <div className="ctl-val">{value}</div>
      <div className="lab">{label}</div>
    </div>
  )
}
